import { ABILITIES, ABILITY_SLOTS, type AbilityId, isAbilityId } from '@/data/abilities';
import { type WeaponId, isWeaponId } from '@/data/weapons';
import type { Inventory } from '@/world/inventory';

/**
 * What has been absorbed into each weapon.
 *
 * Per weapon, like growth, and saved for the same reason: it is part of what
 * makes a particular blade yours rather than a copy of one. Forging a second
 * Rootcleaver would give you neither its levels nor its abilities.
 *
 * Absorption is one-way on purpose. Being able to pull an ability back out
 * would make the slots a loadout screen rather than a decision, and the
 * materials are recoverable by fighting — the thing you cannot get back is
 * the time, which is the cost worth having.
 */

export type AbsorbResult = 'absorbed' | 'already-known' | 'no-slots' | 'missing-materials';

export class WeaponAbilities {
  private readonly byWeapon = new Map<WeaponId, AbilityId[]>();

  of(weapon: WeaponId): readonly AbilityId[] {
    return this.byWeapon.get(weapon) ?? [];
  }

  has(weapon: WeaponId, ability: AbilityId): boolean {
    return this.of(weapon).includes(ability);
  }

  spareSlots(weapon: WeaponId): number {
    return ABILITY_SLOTS - this.of(weapon).length;
  }

  /**
   * Why this cannot be absorbed, or null when it can.
   *
   * Separate from doing it so the UI can explain a greyed-out button rather
   * than simply refusing when pressed.
   */
  blockedReason(
    weapon: WeaponId,
    ability: AbilityId,
    inventory: Inventory,
  ): Exclude<AbsorbResult, 'absorbed'> | null {
    if (this.has(weapon, ability)) return 'already-known';
    if (this.spareSlots(weapon) <= 0) return 'no-slots';

    const definition = ABILITIES[ability];
    if (inventory.countOf(definition.material) < definition.cost) return 'missing-materials';

    return null;
  }

  absorb(weapon: WeaponId, ability: AbilityId, inventory: Inventory): AbsorbResult {
    const blocked = this.blockedReason(weapon, ability, inventory);
    if (blocked) return blocked;

    const definition = ABILITIES[ability];
    inventory.remove(definition.material, definition.cost);

    this.byWeapon.set(weapon, [...this.of(weapon), ability]);
    return 'absorbed';
  }

  /**
   * Give an ability without spending anything.
   *
   * For synthesis and evolution, which have already paid — the materials went
   * into the weapon being consumed. Absorption is the only route that costs.
   */
  grant(weapon: WeaponId, ability: AbilityId): boolean {
    if (this.has(weapon, ability) || this.spareSlots(weapon) <= 0) return false;
    this.byWeapon.set(weapon, [...this.of(weapon), ability]);
    return true;
  }

  /** Forget everything a weapon knew, when the weapon itself is gone. */
  forget(weapon: WeaponId): void {
    this.byWeapon.delete(weapon);
  }

  toSave(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const [weapon, abilities] of this.byWeapon) {
      if (abilities.length > 0) out[weapon] = [...abilities];
    }
    return out;
  }

  static fromSave(value: unknown): WeaponAbilities {
    const absorbed = new WeaponAbilities();
    if (!value || typeof value !== 'object') return absorbed;

    for (const [weapon, abilities] of Object.entries(value as Record<string, unknown>)) {
      if (!isWeaponId(weapon) || !Array.isArray(abilities)) continue;

      // Anything unreadable is dropped rather than kept: an ability that no
      // longer exists would sit in a slot forever, doing nothing and blocking
      // one that does.
      const kept = abilities.filter(isAbilityId).slice(0, ABILITY_SLOTS);
      if (kept.length > 0) absorbed.byWeapon.set(weapon, kept);
    }

    return absorbed;
  }
}
